import type { SearchBarProps } from '@brightdrop/bd-ui';
import { SearchType } from '@brightdrop/bd-ui';
import type {
  MRT_PaginationState,
  MRT_RowSelectionState,
  TableDataController,
} from '@brightdrop/table-ui';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import type {
  BatchSummaryVehicleBoardingSession,
  GetBatchSessionsByIdApiArg,
} from '~/api/generated-api';
import { useGetBatchSessionsByIdQuery } from '~/api/generated-api';
import { selectActingProfile } from '~/store/profile/profileSlice.selectors';
import { DEFAULT_FAILED_VEHICLES_PAGINATION_OPTIONS } from '../utils/failedVehicles.utils';

const FAILED_STATUSES = ['FAILED', 'REJECTED', 'CANCELED', 'OTHER'] as const;

/**
 * Data controller hook for the failed vehicles table modal.
 * Manages pagination (cursor-based via continuation tokens), search, and API data.
 */
export const useFailedVehiclesDataController = (
  organizationsId: string,
  batchId: string,
  isOpen = true
): {
  dataController: TableDataController<BatchSummaryVehicleBoardingSession>;
  searchBarConfig: SearchBarProps;
} => {
  const profile = useSelector(selectActingProfile);
  const fleetId = profile?.fleet?.id;

  const [pagination, setPagination] = useState<MRT_PaginationState>(
    DEFAULT_FAILED_VEHICLES_PAGINATION_OPTIONS
  );
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [continuationTokensByPageIndex, setContinuationTokensByPageIndex] =
    useState<(string | undefined)[]>(['']);

  const currentContinuationToken =
    continuationTokensByPageIndex[pagination.pageIndex];

  const payload: GetBatchSessionsByIdApiArg = {
    'cs-organization': organizationsId,
    ...(fleetId && { 'cs-fleet': fleetId }),
    batchId,
    size: pagination.pageSize,
    status: [...FAILED_STATUSES],
  };
  if (currentContinuationToken) {
    payload['cs-continuation-token'] = currentContinuationToken;
  }
  if (searchTerm && searchTerm.length > 0) {
    payload.vin = searchTerm;
  }

  const { data, isLoading, refetch } = useGetBatchSessionsByIdQuery(payload, {
    skip: !organizationsId || !batchId || !isOpen,
  });

  const newContinuationToken = data?.result?.continuation_token;
  const totalCount = data?.result?.total_items ?? 0;
  const items = data?.result?.items ?? [];

  useEffect(() => {
    if (newContinuationToken) {
      setContinuationTokensByPageIndex((prev) => [
        ...prev.slice(0, pagination.pageIndex + 1),
        newContinuationToken,
      ]);
    }
  }, [newContinuationToken, pagination.pageIndex]);

  const handleSearch = useCallback(
    (_searchType: SearchType, input?: string) => {
      setPagination((prev) => ({
        ...prev,
        pageIndex: DEFAULT_FAILED_VEHICLES_PAGINATION_OPTIONS.pageIndex,
      }));
      setContinuationTokensByPageIndex(['']);
      setSearchTerm(input ?? '');
    },
    []
  );

  const resetSearch = useCallback(() => {
    setPagination((prev) => ({
      ...prev,
      pageIndex: DEFAULT_FAILED_VEHICLES_PAGINATION_OPTIONS.pageIndex,
    }));
    setContinuationTokensByPageIndex(['']);
    setSearchTerm('');
  }, []);

  const dataController: TableDataController<BatchSummaryVehicleBoardingSession> =
    {
      data: items,
      totalCount,
      isLoading,
      pagination,
      setPagination,
      rowSelection: {} as MRT_RowSelectionState,
      setRowSelection: () => {},
      getRowId: (row) => row.id ?? `${row.vin}-${row.reasonCode}`,
      refetch,
    };

  const searchBarConfig: SearchBarProps = {
    onSearch: handleSearch,
    onClear: resetSearch,
    searchInput: searchTerm,
    withoutSearchTypeSelector: true,
    initialSearchType: SearchType.ID,
  };

  return { dataController, searchBarConfig };
};
